import React, { useState } from 'react';
import {
  Phone,
  Mail,
  User,
  Tag,
  Edit3,
  Plus,
  Minus,
  CheckCircle2
} from 'lucide-react';
import './ContactPage.css';

function ContactPage() {
  const [openFaqIndex, setOpenFaqIndex] = useState(0);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    subject: '',
    message: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setFormSubmitted(true);
    setTimeout(() => {
      setFormSubmitted(false);
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        subject: '',
        message: ''
      });
    }, 4000);
  };

  const toggleFaq = (index) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };

  const faqsList = [
    {
      id: 1,
      question: "What age groups and student levels does Deepclariti support?",
      answer: "Deepclariti provides tailored career guidance for Class 7–8 ('Beyond Marks'), Class 9–10 ('Future Pathways'), Class 11–12 ('Career Discovery'), undergraduate & postgraduate college students ('Career Launch' & 'Acceleration'), as well as young working professionals."
    },
    {
      id: 2,
      question: "How do Deepclariti's personalised career coaching sessions work?",
      answer: "Our sessions begin with scientifically validated psychometric assessments, followed by one-on-one guided self-discovery sessions with internationally certified career coach V. Swaminathan to align strengths, interests, and future academic/career pathways."
    },
    {
      id: 3,
      question: "Do you involve parents in the guidance process?",
      answer: "Yes! We place a strong emphasis on parent-student alignment. We conduct dedicated family discussions to foster shared understanding, reduce academic stress, and build confidence around stream and career choices."
    },
    {
      id: 4,
      question: "How can I schedule a consultation session with Deepclariti?",
      answer: "You can easily book a free 45-minute consultation by filling out the contact form above, emailing us at support@deepclariti.com, or calling our student support team directly."
    }
  ];

  return (
    <div className="contact-page padding-y">
      <div className="container">

        {/* ================= SECTION 1: HERO CONTACT FORM & INFO ================= */}
        <div className="contact-hero-grid">

          {/* Left Column: Heading & Quick Info */}
          <div className="contact-hero-left">
            <h1 className="contact-main-title">
              How Can We <span className="title-highlight-italic">Assist</span><br />
              You Today?
            </h1>
            
            <p className="contact-hero-subtitle">
              Our customer support team is here to assist you from Monday to Friday, available daily from 8:00 AM to 8:30 PM.
            </p>

            <hr className="contact-divider" />

            <div className="contact-quick-pills">

              {/* Pill 1: Phone */}
              <div className="contact-quick-pill">
                <div className="pill-icon-box">
                  <Phone size={20} className="pill-icon" />
                </div>
                <div className="pill-text-content">
                  <span className="pill-label">Have any questions</span>
                  <a href="tel:+5551234567" className="pill-value">+(555) 123–4567</a>
                </div>
              </div>

              {/* Pill 2: Email */}
              <div className="contact-quick-pill">
                <div className="pill-icon-box">
                  <Mail size={20} className="pill-icon" />
                </div>
                <div className="pill-text-content">
                  <span className="pill-label">Write mail</span>
                  <a href="mailto:support@deepclariti.com" className="pill-value">support@deepclariti.com</a>
                </div>
              </div>

            </div>
          </div>

          {/* Right Column: Contact Form Card */}
          <div className="contact-form-card">
            <p className="form-card-subtitle">
              Our team of experts is ready to answer your questions, give you more info and help you.
            </p>

            {formSubmitted ? (
              <div className="form-success-banner">
                <CheckCircle2 size={32} className="success-icon" />
                <div>
                  <h4>Thank You!</h4>
                  <p>Your message has been sent successfully. We will get back to you within 1 business day.</p>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="contact-form-element">
                {/* Row 1: First & Last Name */}
                <div className="form-row-2col">
                  <div className="form-group">
                    <label className="form-label">First Name</label>
                    <div className="input-with-icon">
                      <User size={16} className="input-icon" />
                      <input
                        type="text"
                        name="firstName"
                        placeholder="Jhon"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        required
                        className="form-input"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Last Name</label>
                    <div className="input-with-icon">
                      <User size={16} className="input-icon" />
                      <input
                        type="text"
                        name="lastName"
                        placeholder="Roberts"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        required
                        className="form-input"
                      />
                    </div>
                  </div>
                </div>

                {/* Row 2: Email */}
                <div className="form-group">
                  <label className="form-label">Email Address</label>
                  <div className="input-with-icon">
                    <Mail size={16} className="input-icon" />
                    <input
                      type="email"
                      name="email"
                      placeholder="jhon@roberts.com"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="form-input"
                    />
                  </div>
                </div>

                {/* Row 3: Subject */}
                <div className="form-group">
                  <label className="form-label">Subject</label>
                  <div className="input-with-icon">
                    <Tag size={16} className="input-icon" />
                    <input
                      type="text"
                      name="subject"
                      placeholder="Career Coaching Query"
                      value={formData.subject}
                      onChange={handleInputChange}
                      required
                      className="form-input"
                    />
                  </div>
                </div>

                {/* Row 4: Message */}
                <div className="form-group">
                  <label className="form-label">Message</label>
                  <div className="input-with-icon textarea-wrapper">
                    <Edit3 size={16} className="input-icon textarea-icon" />
                    <textarea
                      name="message"
                      placeholder="Write how can we help you..."
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      rows={4}
                      className="form-textarea"
                    ></textarea>
                  </div>
                </div>

                <button type="submit" className="btn-send-message">
                  Send Message
                </button>
              </form>
            )}
          </div>

        </div>

        {/* ================= SECTION 2: FAQS SECTION ================= */}
        <div className="contact-faqs-section">
          <div className="contact-faqs-grid">

            {/* Left Column: Heading */}
            <div className="faqs-left-col">
              <h2 className="faqs-title">
                Deepclariti Career<br />
                Guidance – FAQs
              </h2>
              <p className="faqs-subtitle">
                Answers to common questions about our personalised career coaching, assessments, and student guidance programs.
              </p>
            </div>

            {/* Right Column: Accordion List */}
            <div className="faqs-right-col">
              <div className="contact-accordion-list">
                {faqsList.map((faq, idx) => {
                  const isOpen = openFaqIndex === idx;
                  return (
                    <div key={faq.id} className={`contact-faq-item ${isOpen ? 'open' : ''}`}>
                      <button
                        className="faq-header-btn"
                        onClick={() => toggleFaq(idx)}
                      >
                        <span className="faq-question-text">{faq.question}</span>
                        <span className="faq-toggle-icon">
                          {isOpen ? <Minus size={18} /> : <Plus size={18} />}
                        </span>
                      </button>

                      {isOpen && (
                        <div className="faq-body-content">
                          <p>{faq.answer}</p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

          </div>
        </div>

        {/* ================= SECTION 3: MAP & CONTACT DETAILS ================= */}
        <div className="contact-map-details-section">
          <div className="map-details-grid">

            {/* Left Column: Map Container */}
            <div className="contact-map-box">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3988.7905872895697!2d103.6334!3d1.3214!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x31da0f60714b7bbd%3A0x6b4f7b6d19f6a1e0!2sTuas%20South%20Ave%2010!5e0!3m2!1sen!2ssg!4v1700000000000!5m2!1sen!2ssg"
                width="100%"
                height="100%"
                style={{ border: 0, borderRadius: '20px', minHeight: '380px' }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Deepclariti Location Map"
              ></iframe>
            </div>

            {/* Right Column: Contact Details */}
            <div className="contact-details-box">
              <h3 className="contact-details-heading">CONTACT DETAILS</h3>
              
              <p className="contact-details-desc">
                Whether you're looking for detailed information, need a comprehensive questions about how we can meet your unique requirements.
              </p>

              <div className="contact-info-list">
                {/* Location */}
                <div className="contact-info-block">
                  <span className="info-block-label">Our Location</span>
                  <p className="info-block-text">#02-05, 9 Tuas South Avenue 10 Singapore 637014</p>
                </div>

                {/* Message with us */}
                <div className="contact-info-block">
                  <span className="info-block-label">Message with us</span>
                  <p className="info-block-sublabel">Within 1 Business Day</p>
                  <a href="mailto:solar@traverse.sg" className="info-block-link">solar@traverse.sg</a>
                </div>

                {/* Call us now */}
                <div className="contact-info-block">
                  <span className="info-block-label">Call us now</span>
                  <a href="tel:+6567340510" className="info-block-link">+65 6734 0510</a>
                </div>
              </div>

              {/* Social Media Links */}
              <div className="contact-social-pills">
                <a href="https://x.com" target="_blank" rel="noopener noreferrer" className="social-pill" aria-label="X">
                  <span className="social-icon-symbol">X</span>
                </a>
                <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" className="social-pill" aria-label="Facebook">
                  <span className="social-icon-symbol social-fb">f</span>
                </a>
                <a href="https://youtube.com" target="_blank" rel="noopener noreferrer" className="social-pill" aria-label="YouTube">
                  <span className="social-icon-symbol social-yt">▶</span>
                </a>
                <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="social-pill" aria-label="Instagram">
                  <span className="social-icon-symbol social-ig">📷</span>
                </a>
              </div>

            </div>

          </div>
        </div>

      </div>
    </div>
  );
}

export default ContactPage;
